import { map, flatten, every } from "lodash";
import { XAxisOptions } from "highcharts";
import { convertColor, logExportPptFeature } from "./common";

/**
 * Returns a configuration object for PPT Export
 *
 * @param formatLabel
 *    Allows to override the way the label is gathered :
 *      if Highcharts.Options object has xAxis, the method will receive the "xAxis.category" value
 *      else the series.data field will be provided.
 */
export function getCommonPptExport(
  chartType: string,
  chartOptions: Highcharts.Options | undefined,
  formatLabel?: (input: any) => string,
  showLegend = true,
  horizontal = false
): any {
  if (!chartOptions) {
    return undefined;
  }

  console.log("source data", chartOptions);
  const data = buildExportData(chartOptions, formatLabel);
  const options = buildExportOptions(
    chartOptions,
    showLegend,
    horizontal,
    chartType
  );
  console.log("Params", { data, options });

  logExportPptFeature();
  return {
    type: "chart",
    chartType,
    data,
    options,
  };
}

export function buildExportData(
  data: Highcharts.Options,
  formatLabel?: (input: any) => string
): any[] {
  return map(data.series, serie => ({
    name: serie.name ? serie.name.toString() : "",
    labels: getLabels(data, serie, formatLabel),
    values: map(
      serie.data,
      point =>
        point.y !== undefined
          ? point.y
          : point.data !== undefined
          ? point.data
          : point // Depending on source, point can be an object with value in "y" or "data", or simple a number
    ),
  }));
}

export function buildExportOptions(
  data: Highcharts.Options,
  showLegend = true,
  horizontal = false,
  chartType = ""
): any {
  return {
    h: "70%",
    w: "80%",
    chartColors: getColors(data),
    legendPos: chartType === "pie" ? "r" : "b",
    showLegend,
    showValAxisTitle: getAxisTitle(data) !== "",
    valAxisTitle: getAxisTitle(data),

    // Bar chart options
    barDir: horizontal === true ? "bar" : "col",

    // Line chart options
    lineSmooth: true,
  };
}

function getColors(data: Highcharts.Options): string[] {
  const colorsFromSeries = flatten(
    map(data.series, serie => {
      if (serie.color) {
        return convertColor(serie.color);
      }
      if (serie.data && serie.data[0].color) {
        return map(serie.data, entry => convertColor(entry.color));
      }
      return "";
    })
  );
  if (
    colorsFromSeries.length > 0 &&
    every(colorsFromSeries, entry => entry === "") &&
    data.colors
  ) {
    return map(data.colors, color => convertColor(color));
  } else {
    return colorsFromSeries;
  }
}

function getLabels(
  data: Highcharts.Options,
  serie: any,
  formatLabel?: (input: any) => string
): any[] {
  if (data.xAxis && (data.xAxis as XAxisOptions).categories) {
    if (formatLabel) {
      return map((data.xAxis as XAxisOptions).categories, category =>
        formatLabel(category)
      );
    } else {
      return (data.xAxis as XAxisOptions).categories as string[];
    }
  } else {
    return map(serie.data, serieData =>
      formatLabel ? formatLabel(serieData) : serieData.name
    );
  }
}

function getAxisTitle(data: Highcharts.Options): string {
  if (!data.yAxis) {
    return "";
  }

  if (Array.isArray(data.yAxis)) {
    const firstAxis = data.yAxis[0];
    return firstAxis.title && firstAxis.title.text ? firstAxis.title.text : "";
  } else {
    const axis = data.yAxis;
    return axis.title && axis.title.text ? axis.title.text : "";
  }
}
